// Layer 4 (UI) — the Process route (polish.P17 #704, locked comp
// `.impeccable/mocks/decision/process.html` A): one header row with the mode
// changes this shift, one status band, pill tabs with their headlines, then an
// 8/4 row per tab. Loop status is the default tab for every persona; only a
// persona with `case:evidence` (Control room) gets the answer buttons.
// Every figure comes from Scene.transform.ts.

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDrawer } from '@/app/providers';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import {
  ASSIGNEE_SHORT,
  casesWithSession,
} from '@/versions/v1/modules/advisory/Scene.transform';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { LoopStatusTab } from './LoopStatus';
import { ProcessBand } from './ProcessBand';
import { ReadingsTab } from './Readings';
import {
  PROCESS_TABS,
  type ModeChangesFact,
  type ProcessTab,
  compactReadingRows,
  controlLoops,
  defaultSelectedLoopId,
  defaultSelectedReadingId,
  evidenceAnsweredAtLabel,
  heldByLoop,
  loopEvidenceItems,
  loopException,
  loopRows,
  loopTags,
  modeChangesThisShift,
  operatorReadingsTable,
  processBand,
  processCopy,
  processTabHeadlines,
  readingIdForRow,
  readingRows,
} from './Scene.transform';

const HELP_DOC: Record<ProcessTab, string> = {
  'loop-status': 'process/loop-status',
  readings: 'process/readings',
};

const shortRole = (roleId: keyof typeof ASSIGNEE_SHORT) => ASSIGNEE_SHORT[roleId];

export function ProcessScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const copy = processCopy();
  const [tab, setTab] = useState<ProcessTab>('loop-status');
  const { state } = useScenario();
  const advisory = useAdvisory();
  const { can } = useRole();
  const { open: openDrawer } = useDrawer();

  const loops = useMemo(() => controlLoops(), []);
  const table = useMemo(() => operatorReadingsTable(), []);
  const cases = useMemo(() => casesWithSession(state, advisory), [state, advisory]);
  const evidence = useMemo(() => loopEvidenceItems(cases, loopTags(loops)), [cases, loops]);
  const rows = useMemo(() => loopRows(loops, state, evidence, shortRole), [loops, state, evidence]);
  const readings = useMemo(() => (table ? readingRows(table, state) : []), [table, state]);
  const band = processBand(loops, readings, evidence, state.cursor);
  const modeChanges = modeChangesThisShift(loops, state.cursor, shortRole);
  const headlines = processTabHeadlines(loops, readings);
  const tabItems = PROCESS_TABS.map((item) => ({ ...item, count: headlines[item.id] }));

  const [loopId, setLoopId] = useState(() => defaultSelectedLoopId(loops));
  const [readingId, setReadingId] = useState(() => defaultSelectedReadingId(readings));
  const selectedLoop = loops.find((loop) => loop.id === loopId) ?? loops[0];
  const exception = selectedLoop ? loopException(selectedLoop, cases) : {};
  const heldBy = heldByLoop(readingIdForRow(readingId), loops, evidence);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const openCase = (caseId: string, title: string) =>
    openDrawer('case-drawer', { kicker: 'Case', title, wide: true, data: { caseId } });

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProcessScene">
        <SurfacePageBody className="@container min-h-[calc(100vh-3.2rem)] space-y-3" width="full">
          <ProcessHeader title={copy.title} fact={modeChanges} helpDoc={HELP_DOC[tab]} />
          <ProcessBand cells={band} onOpenCase={openCase} />
          <SurfacePageTabs aria-label="Process" items={tabItems} value={tab} onChange={setTab} variant="ink" className="pt-1" />
          {tab === 'loop-status' ? (
            <LoopStatusTab
              rows={rows}
              selectedId={loopId}
              onSelect={setLoopId}
              exception={exception}
              evidence={evidence}
              canAnswer={can('case:evidence')}
              answeredAtLabel={(item) => evidenceAnsweredAtLabel(item.answeredAt, state.cursor)}
              onAnswer={(evidenceId, answer) => advisory.answerEvidence(evidenceId, answer)}
              onOpenCase={openCase}
            />
          ) : null}
          {tab === 'readings' && table ? (
            <ReadingsTab
              caption={table.caption}
              rows={readings}
              compact={compactReadingRows(table, readingsAt(state))}
              selectedId={readingId}
              onSelect={setReadingId}
              heldBy={heldBy}
              onOpenCase={openCase}
            />
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** The page's one header row (comp `.ptitle`): the title, then the shift's
 *  mode changes as a header fact, then the help anchor. */
function ProcessHeader({ title, fact, helpDoc }: { title: string; fact: ModeChangesFact; helpDoc: string }) {
  return (
    <header className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'mr-auto min-w-0')}>{title}</h1>
      <div className="flex items-center gap-3">
        <dl className="border-l border-[var(--hairline-strong)] pl-4 tabular-nums" data-header-fact="mode-changes">
          <dt className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">{fact.label}</dt>
          <dd className="text-lg font-semibold leading-snug text-ink-primary">{fact.count}</dd>
          <dd className="whitespace-nowrap text-xs text-muted">
            {fact.latest ? (
              <>
                <span className="font-semibold text-ink-secondary">{fact.latest.tag}</span>
                {fact.latest.text}
                {fact.latest.by ? ` · ${fact.latest.by}` : ''}
              </>
            ) : (
              <>
                vs <span className="font-semibold text-ink-secondary">{fact.heldCount}</span> loops held
              </>
            )}
          </dd>
        </dl>
        <HelpAnchor doc={helpDoc} label="Process" />
      </div>
    </header>
  );
}
